import DeviceControls from 'features/Integrations/components/DeviceControls/DeviceControls';
import DeviceHistory from 'features/Integrations/components/DeviceHistory/DeviceHistory';
import GroupSettingsDeviceList from 'features/Integrations/features/ConnectedDeviceGroups/components/GroupSettingsDeviceList/GroupSettingsDeviceList';
import BaseMap from 'components/BaseMap/BaseMap';
import BaseModal from 'components/Modals/BaseModal';
import TextToggle from 'components/UI/TextToggle';
import { usePerms } from 'contexts/PermsContext';
import layout from 'data/DeviceControls.json';
import {
  DEVICE_HISTORY_PAGE_SIZE,
  DEVICE_HISTORY_START_MS,
} from 'features/Integrations/config/deviceHistory.config';
import {
  buildGroupCommandArgs,
  buildGroupCommandDraft,
  buildGroupHistoryRows,
  isSirenDevice,
  resolveGroupDevices,
} from '../../utils/settings.utils';
import { getAverageGroupPosition } from '../../utils/groups.utils';
import useGroupCommandDraft from '../../hooks/useGroupCommandDraft';
import AudioManager from 'features/Integrations/features/AudioManager/index.web';
import useDeviceSeriesData from 'features/Integrations/hooks/useDeviceSeriesData';
import {
  defineGroupType,
  isDeviceTypeAllowed,
} from 'features/Integrations/utils/deviceGroupRules.utils';
import VideoPlayer from 'features/VMS/features/VideoPlayer/index.web';
import L from 'leaflet';
import { useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { Marker, Polyline } from 'react-leaflet';
import { sendGroupCommand } from 'services/devices/commands/commands.service';
import {
  useGetConnectedDevicesQuery,
  useKillswitchDeviceMutation,
} from 'services/devices/devices.api';
import { getDynamicDeviceIcon } from 'utils/devices/icon.utils';
import { notifyErrorToast } from 'utils/notifications/toast.utils';
import DevicePinPopup from './components/DevicePinPopup/DevicePinPopup';

/**
 * @param {{group: object, onSelectDevice: (id: number), onClose?: ()}}
 */
const DeviceGroupSettings = ({ group, onSelectDevice }) => {
  const { data: connectedDevices = [] } = useGetConnectedDevicesQuery();
  const [killswitchDevice] = useKillswitchDeviceMutation();

  const perms = usePerms();

  const groupDevices = useMemo(
    () => resolveGroupDevices(connectedDevices, group),
    [connectedDevices, group],
  );
  const groupType = defineGroupType(group?.devices, connectedDevices, group?.type);
  const macroEligible =
    groupDevices.length > 0 &&
    groupDevices.every((device) => isDeviceTypeAllowed(device?.device_type, groupType));

  // The history table lists each member's command events over time, and the
  // page load holds ONE row for each device. Fetch the newest rows for the
  // members when this opens; they merge into the device cache the table reads.
  // See DEVICE_HISTORY_START_MS for why the window starts at the epoch.
  const {
    isFetching: historyLoading,
    hasMore: historyHasMore,
    loadMore: loadMoreHistory,
    error: historyError,
  } = useDeviceSeriesData({
    ids: groupDevices.map((device) => device.id),
    startTime: DEVICE_HISTORY_START_MS,
    limit: DEVICE_HISTORY_PAGE_SIZE,
  });

  const avgPosition = useMemo(() => getAverageGroupPosition(groupDevices), [groupDevices]);
  const groupHistoryData = useMemo(() => buildGroupHistoryRows(groupDevices), [groupDevices]);
  const commandSeed = useMemo(
    () =>
      macroEligible
        ? buildGroupCommandDraft(
            groupDevices.find((device) => device.device_type === groupType) || groupDevices[0],
            groupType,
            groupHistoryData,
          )
        : null,
    [groupDevices, groupType, macroEligible, groupHistoryData],
  );
  const [commandDraft, setCommandDraft, startCommandSend] = useGroupCommandDraft(
    group?.id,
    commandSeed,
  );
  const [showVideo, setShowVideo] = useState(false);
  const [sending, setSending] = useState(false);
  const [audioManager, setAudioManager] = useState(false);
  // A temporary file belongs to the group it was chosen for. Reading it through the group id
  // keeps it from the next group's controls, which mount before the reset effect below runs.
  const [audioOverrideChoice, setAudioOverrideChoice] = useState({ groupId: null, audio: null });
  const audioOverride =
    audioOverrideChoice.groupId === group?.id ? audioOverrideChoice.audio : null;

  const paSirenIds = useMemo(
    () =>
      groupDevices
        .filter((device) => isSirenDevice(device) && device.camera)
        .map((device) => device.id),
    [groupDevices],
  );

  useEffect(() => {
    setAudioManager(false);
  }, [group?.id]);

  const typeToPermMap = {
    'Technology Bridge_Flashing Lights': 'RescAlertDevicesTechnologyBridge_FlashingLights',
    'Technology Bridge_Siren/Giant Voice': 'RescAlertDevicesTechnologyBridge_Siren-GiantVoice',
  };

  const createEditPermission = () => {
    if (!group.type) return true;

    const permissionType = typeToPermMap[group.type];
    if (!permissionType) return true;

    // A group command is its own permission (2026-08-28): SendDeviceGroupCommands.
    return perms.testPermission([], ['SendDeviceGroupCommands']);
  };

  const renderDevice = (deviceId, index) => {
    const device = groupDevices.find((device) => device.id === deviceId);

    if (device)
      return (
        <Marker
          key={deviceId + '' + index}
          position={device.location}
          icon={getDynamicDeviceIcon(device, 'icon')}
        >
          <DevicePinPopup device={device} onSelect={onSelectDevice} />
        </Marker>
      );
  };

  const renderLine = (line) => {
    return (
      <>
        <Polyline positions={line.coordinates} color={'#FF0000'} />

        {line.coordinates.map((point) => (
          <Marker
            position={point}
            icon={
              new L.DivIcon({
                className: 'custom-x-icon',
                html: `<div style="color: red; font-size: 30px; position: absolute; top: -4px; left: -2px;">X</div>`,
                iconSize: [20, 20],
                iconAnchor: [10, 16],
              })
            }
          />
        ))}
      </>
    );
  };

  const switchMaintenanceMode = async (device, kill_switchValue) => {
    if (!perms.isMaintenanceOperator()) return;

    try {
      await killswitchDevice({ id: device.id, state: kill_switchValue }).unwrap();
    } catch (err) {
      console.error('Error sending group command:', err.message);
      notifyErrorToast('maintenance mode', { isAddOperation: false });
    }
  };

  const groupSwitchMaintenanceMode = () => {
    const devices = groupDevices;
    //If any device has a kill switch enabled
    if (devices.some((device) => device.kill_switch === 1)) {
      //Disable all kill switches
      devices
        .filter((_device) => _device.kill_switch === 1) //Isolate only the enabled ones
        .map((_device) => switchMaintenanceMode(_device, 0));
    } else {
      //Enable all kill switches
      devices.map((_device) => switchMaintenanceMode(_device, 1));
    }
  };

  const videoPlayerLabel = `${group?.name || group?.alias || group?.id || 'Device group'} camera feed`;

  const deviceControlListener = async (evt, data, details) => {
    switch (evt) {
      case 'update':
        setCommandDraft(data, details?.editedFields);
        return;
      case 'submit':
        try {
          setSending(true);
          const finishCommandSend = startCommandSend();
          // The group's file, volume and repeat now travel with the command (the backend used to drop them).
          await sendGroupCommand(group.id, data.status, data.comments, buildGroupCommandArgs(data));
          finishCommandSend();
          setSending(false);
        } catch (err) {
          console.error('Error sending device command:', err.message);
          setSending(false);
        }

        //Disable on close when command is sent.
        // onClose();
        return;
      case 'cancel-audio':
        try {
          // A maintenance operator's stop command carries the override (see DeviceControls).
          if (data?.maintenanceOverride === true) {
            await sendGroupCommand(group.id, 7, '', { maintenanceOverride: true });
          } else {
            await sendGroupCommand(group.id, 7, '');
          }
        } catch (err) {
          console.error('Error sending device command:', err.message);
        }
        return;
      case 'audio-manager':
        setAudioManager(true);
        return;
      default:
        return;
    }
  };

  const onSetTempAudio = (fileName) => {
    setAudioOverrideChoice({
      groupId: group?.id,
      audio: {
        id: -1,
        name: fileName,
        displayName: fileName,
        relay_id: 1,
        device_ids: null,
      },
    });

    setAudioManager(false);
  };

  return (
    <div className="d-flex flex-column min-h-75vh gap-2 overflow-y-auto overflow-x-hidden">
      {/* Top */}
      <div className="d-flex flex-column flex-lg-row flex-fill flex-basis-0 gap-2">
        <div className="d-flex flex-column flex-fill flex-basis-0">
          {avgPosition && (
            <div className="d-flex flex-fill flex-column min-h-250">
              {!showVideo && (
                <div className="base-map-fill-wrapper">
                  <BaseMap
                    mapCenter={[avgPosition?.lat, avgPosition?.lng]}
                    zoom={10}
                    minZoom={0}
                    maxZoom={19}
                    scrollZoom
                    touchZoom
                    style={{ zIndex: 0 }}
                  >
                    {/* check for coordinates existing before mapping */}
                    {group?.coords && group?.coords.map(renderLine)}

                    {group?.devices && group.devices.map(renderDevice)}
                  </BaseMap>
                </div>
              )}

              {showVideo && group?.aux_devices?.length > 0 && (
                <div className="d-flex flex-fill">
                  <VideoPlayer
                    ariaLabel={videoPlayerLabel}
                    src={`${process.env.PUBLIC_API_URL}/v2/integrations/device/camera/${encodeURIComponent(group.aux_devices[0])}/video-stream/index.m3u8`}
                  />
                </div>
              )}

              {group?.aux_devices?.length > 0 &&
                perms.testPermission(
                  [8, 9, 10, 11, 12],
                  [`ViewExacqVisionOverwatchVMSConnections`],
                ) && (
                  <div className="mt-2">
                    <TextToggle
                      options={['Map', 'Video']}
                      value={showVideo}
                      setValue={setShowVideo}
                    />
                  </div>
                )}
            </div>
          )}

          {macroEligible && perms.isMaintenanceOperator() && layout[groupType?.toLowerCase()] && (
            <Button
              variant={`${
                groupDevices.some((device) => device.kill_switch || device.data?.status === -1)
                  ? 'warning'
                  : 'primary'
              } mt-1 border border-black`}
              onClick={groupSwitchMaintenanceMode}
            >
              {groupDevices.some((device) => device.kill_switch || device.data?.status === -1)
                ? 'Disable Maintenance Mode'
                : 'Enable Maintenance Mode'}
            </Button>
          )}
        </div>

        {macroEligible &&
          commandDraft &&
          createEditPermission() &&
          layout[groupType?.toLowerCase()] && (
            <div className="flex-fill flex-basis-0">
              <DeviceControls
                key={group?.id}
                device={commandDraft}
                devices={groupDevices}
                sendsGroupCommand
                on={deviceControlListener}
                sending={sending}
                audioOverride={audioOverride}
              />
            </div>
          )}
      </div>

      {/* Bottom */}
      <div className="d-flex flex-column flex-lg-row flex-fill flex-basis-0 gap-2">
        {macroEligible && layout[groupType?.toLowerCase()] && (
          <div className="d-flex flex-fill flex-basis-0">
            <DeviceHistory
              title={'Group Device History'}
              data={groupHistoryData}
              deviceType={groupType}
              loading={historyLoading}
              hasMore={historyHasMore}
              onLoadMore={loadMoreHistory}
              error={historyError}
            />
          </div>
        )}

        <div className="d-flex flex-fill flex-basis-0">
          <GroupSettingsDeviceList
            devices={groupDevices}
            onSelect={onSelectDevice}
            highlightIds={commandDraft?.audio?.device_ids ? [...commandDraft.audio.device_ids] : []}
          />
        </div>
      </div>

      {/* Audio Manager */}
      <BaseModal
        show={perms.testPermission([], ['ViewSirenAudioFiles']) && audioManager}
        onHide={() => setAudioManager(false)}
        stackLevel={2}
      >
        <AudioManager
          deviceIds={paSirenIds}
          onClose={() => setAudioManager(false)}
          onPlayTemp={onSetTempAudio}
        />
      </BaseModal>
    </div>
  );
};

export default DeviceGroupSettings;
